// ─────────────────────────────────────────────────────────────────────────────
// Market guide page
// ─────────────────────────────────────────────────────────────────────────────

import {
  GuideLayout, H2First, H2, P, UL, LI, UI,
  ScreenshotFrame, Callout,
} from "../GuideLayout";

export function GuideMarket() {
  return (
    <GuideLayout
      title="Market"
      subtitle="Live economic calendar and pre-market brief. Tells you when high-impact news is approaching, so you can stay out of trades the market is about to dictate the outcome of."
      prev={{ slug: "strategies",  label: "Strategies" }}
      next={{ slug: "profile",     label: "Profile Settings" }}
    >
      <H2First>What it's for</H2First>
      <P>
        Most blow-ups on news days aren't from trades that lost — they're
        from trades that should never have been on. A clean technical
        setup becomes a coin flip when CPI hits at 8:30am, and "good
        trade, bad outcome" is a hard story to tell when you knew the
        event was coming.
      </P>
      <P>
        Market is the calendar that surfaces those windows before they
        happen. Live USD economic events, ranked by impact, with a
        running status read on whether the next 45 minutes is safe to
        trade.
      </P>

      <ScreenshotFrame
        src="/guide/market-overview.png"
        alt="Market Intelligence page with status banner, today's events, and pre-market brief"
        caption="Status banner at the top reflects the next 45 minutes; events and the brief below it."
      />

      <H2>The status banner</H2>
      <P>
        Below the header, a colored banner gives a one-glance read on
        current trading conditions:
      </P>
      <UL>
        <LI><strong style={{ color: "#00e676" }}>✓ SAFE TO TRADE</strong> — the next high-impact event is more than 45 minutes out</LI>
        <LI><strong style={{ color: "#ffd600" }}>⚠ CAUTION</strong> — a high-impact event is within 45 minutes</LI>
        <LI><strong style={{ color: "#ff1744" }}>✕ NO TRADE ZONE</strong> — a high-impact event is within 15 minutes</LI>
      </UL>
      <P>
        When the status is anything other than SAFE, a live countdown
        timer on the right side of the banner ticks down second by
        second to the event — no page refresh needed. The banner shifts
        color automatically as the countdown crosses each threshold.
      </P>

      <H2>Market Sentiment (AI-generated)</H2>
      <P>
        Below the status banner, a sentiment widget summarizes the
        broader market read in plain English — current posture
        (risk-on, neutral, risk-off), a short summary of what's
        driving it, and links to the sources it draws from. Updates
        throughout the trading day.
      </P>

      <Callout kind="info" title="AI sentiment vs. your judgment">
        The sentiment read can be wrong. Use it to check your
        assumptions rather than form them.
      </Callout>

      <H2>Weekly Risk Heatmap</H2>
      <P>
        Below the sentiment widget, a 7-day grid shows event density
        for the current and upcoming week at a glance. Each day's cell
        is color-coded by risk level:
      </P>
      <UL>
        <LI>Red cells — one or more high-impact events that day</LI>
        <LI>Yellow cells — medium-impact events only</LI>
        <LI>Faint cells — low-impact or no events</LI>
      </UL>
      <P>
        Hover any cell to see a tooltip listing the specific
        high and medium-impact events for that day. Today's cell has a
        blue border. Use this for weekly planning — knowing Wednesday
        is red before Monday lets you size your week's risk accordingly.
      </P>

      <H2>Today's Events</H2>
      <P>
        Below the heatmap, a list of every USD-impacting event
        scheduled for the current trading day. Each row shows time
        (ET), event name, forecast, previous reading, actual (once
        released), and an impact pill — high (red), medium (yellow),
        or low (blue).
      </P>
      <P>
        By default only high-impact events are shown. A{" "}
        <UI>Medium OFF / ON</UI> toggle in the section header adds
        medium-impact events to the list. When the toggle is off and
        medium events exist, the empty state tells you how many are
        hidden rather than showing a false "clean day" message.
      </P>
      <P>
        When an event has already released, the Actual column shows
        the result alongside the forecast. If the actual differs
        meaningfully from forecast, the delta is shown in green
        (beat) or red (missed) next to the actual value.
      </P>

      <H2>Surprise Tracker</H2>
      <P>
        Between Today's Events and the Pre-Market Brief, the Surprise
        Tracker card shows events from the current week that have
        already released with both an actual and a forecast value.
        Events are sorted by largest absolute miss first — the biggest
        deviations from consensus expectation appear at the top.
      </P>
      <P>
        Each row shows the event name, its impact level, the
        forecast vs actual values, and a colored delta: green for
        better-than-expected, red for worse. The Surprise Tracker is
        hidden on days when no released events have both actual and
        forecast data.
      </P>

      <Callout kind="info">
        Large surprises — especially on CPI, NFP, or FOMC — are the
        events most likely to cause abnormal volatility that persists
        for the rest of the session. A quick check of the Surprise
        Tracker after a morning release tells you whether the day
        has a clean tape or a reaction-driven one.
      </Callout>

      <H2>Full Week Calendar</H2>
      <P>
        Below the Surprise Tracker, a <UI>Full Week Calendar</UI>{" "}
        section with a <UI>Show</UI> toggle expands to reveal the
        current and next week's events. The same{" "}
        <UI>Medium OFF / ON</UI> toggle applies here — when the week
        view is open, the toggle controls both Today's Events and
        the full week simultaneously. Days with hidden medium events
        show a count in the day label so nothing feels invisible.
      </P>

      <H2>Pre-Market Brief</H2>
      <P>
        Below the calendar, the Pre-Market Brief summarizes today's
        risk in two rows:
      </P>
      <UL>
        <LI>
          <strong>High-impact event summary</strong> — count and
          names of today's high-impact events with their scheduled
          times. Shows a "Clean calendar — good conditions to trade
          your setups" note when there's nothing high-impact scheduled.
        </LI>
        <LI>
          <strong>Volatility windows</strong> — when high-impact events
          are scheduled today, the brief computes a ±15 minute /
          +30 minute window around each one and lists those time ranges
          explicitly (e.g. "8:15–9:15 ET (CPI MoM)"). These are the
          windows where volatility is most likely to be event-driven
          rather than technical. When no high-impact events are
          scheduled, a standard best-practice window recommendation
          shows instead. Any medium-impact events today are listed as
          a secondary note beneath the volatility windows.
        </LI>
      </UL>

      <Callout kind="tip">
        The volatility windows in the Pre-Market Brief are the fastest
        way to decide whether to take the first trade of the day or
        wait. If your usual entry time falls inside one of those
        windows, the brief tells you before you have to think about it.
      </Callout>

      <H2>How Market connects to other surfaces</H2>
      <UL>
        <LI><strong>Dashboard's Safe-to-Trade banner</strong> — same status calculation as Market's top banner. Market is the expanded view.</LI>
        <LI><strong>Dashboard's Sentiment widget</strong> — same AI-generated widget, same source.</LI>
        <LI><strong>Trading Rules → Event Blackouts</strong> — news avoidance rules reference event types from this calendar.</LI>
        <LI><strong>Trade Entry's Pre-Trade stage</strong> — "Trade during or around a significant news event" checkbox feeds the News Event Trading detector in Analytics.</LI>
        <LI><strong>"Your Trading Around Events" panel</strong> — uses the same cached event data as the calendar for retrospective analytics.</LI>
      </UL>

      <H2>Your Trading Around Events</H2>
      <P>
        At the bottom of the Market page is a behavioral analytics
        panel comparing your historical performance on high-impact
        event days versus all other trading days.
      </P>
      <P>
        Two controls scope the analysis:
      </P>
      <UL>
        <LI><strong>Account</strong> — a specific account or "All Accounts"</LI>
        <LI><strong>Look back</strong> — 3 months, 6 months, or 1 year</LI>
      </UL>
      <P>
        The panel shows two views:
      </P>
      <UL>
        <LI><strong>Headline comparison</strong> — win rate, total P&L, and average P&L on high-impact event days vs all other days, with a verdict line and small-sample caveats when applicable.</LI>
        <LI><strong>Breakdown by event class</strong> — a row for each event type you've traded around (FOMC, CPI, NFP, PPI, etc.) with win rate, trade count, and total P&L. Classes with fewer than 3 trades are hidden.</LI>
      </UL>

      <Callout kind="info" title="What counts as an event day">
        Any day with at least one high-impact USD economic event.
        Medium and low-impact events are excluded. Day matching is by
        ET calendar day.
      </Callout>

      <H2>How to use it</H2>
      <UL>
        <LI><strong>Morning routine</strong> — check the status banner, scan Today's Events, read the Pre-Market Brief. 30 seconds of context that prevents the day's first bad decision.</LI>
        <LI><strong>Mid-session</strong> — when the countdown timer shows under 15 minutes, decide whether to exit open positions or stay flat through the release.</LI>
        <LI><strong>After a release</strong> — check the Surprise Tracker to see how the actual compared to consensus before deciding whether to trade into the move.</LI>
        <LI><strong>Sunday/Monday morning</strong> — expand the Full Week Calendar and scan the heatmap to plan around the week's high-impact releases.</LI>
      </UL>
    </GuideLayout>
  );
}
